<template>
  <div class="farm-prediction-view">
    <div class="prediction-header">
      <h2 class="prediction-title">Predicción de finca</h2>
      <p v-if="finca?.name" class="prediction-subtitle">{{ finca.name }} ({{ finca.code }})</p>
    </div>

    <div v-if="loading" class="prediction-state">Cargando predicciones…</div>
    <div v-else-if="error" class="prediction-state prediction-state--error">{{ error }}</div>
    <div v-else-if="!displayZones.length" class="prediction-state">
      Esta finca no tiene zonas configuradas.
    </div>

    <template v-else>
      <div class="prediction-kpis">
        <div class="kpi-card">
          <span class="kpi-label">Acumulado finca ({{ horizonDays }} días)</span>
          <span class="kpi-value">{{ formatKg(farmTotalKg) }}</span>
        </div>
        <div class="kpi-card">
          <span class="kpi-label">Total recogido acumulado</span>
          <span class="kpi-value">{{ formatKg(farmTotalActualKg) }}</span>
        </div>
        <div class="kpi-card kpi-card--highlight">
          <span class="kpi-label">Próxima recolección</span>
          <span class="kpi-value">{{ nextHarvestLabel }}</span>
        </div>
        <div class="kpi-card">
          <span class="kpi-label">Score medio finca</span>
          <span class="kpi-value">{{ farmAverageScore }}</span>
        </div>
      </div>

      <div class="sort-toolbar">
        <span class="sort-toolbar__label">Ordenar por:</span>
        <button
          v-for="option in sortOptions"
          :key="option.value"
          type="button"
          class="sort-btn"
          :class="{ 'sort-btn--active': sortBy === option.value }"
          @click="sortBy = option.value"
        >
          {{ option.label }}
        </button>
      </div>

      <p class="prediction-hint">
        Pulsa una zona para ver el detalle diario. La zona resaltada es la siguiente en la rotación de recolección.
      </p>

      <div class="zone-summary-list">
        <button
          v-for="item in sortedZones"
          :key="item.zone.id"
          type="button"
          class="zone-summary-card"
          :class="{
            'zone-summary-card--next': item.is_next_harvest,
            'zone-summary-card--empty': !item.prediction_days,
          }"
          @click="selectZone(item.zone)"
        >
          <div class="zone-summary-card__top">
            <div class="zone-summary-card__header">
              <span class="zone-summary-card__name">{{ item.zone.name || item.zone.code }}</span>
              <div class="zone-summary-card__tags">
                <span v-if="item.is_next_harvest" class="zone-summary-card__badge">Próxima</span>
                <span
                  v-if="item.prediction_days && item.contribution_percent != null"
                  class="zone-summary-card__contrib"
                >
                  {{ formatPercent(item.contribution_percent) }} finca
                </span>
              </div>
            </div>
            <svg
              v-if="item.daily_weights?.length"
              class="zone-sparkline"
              viewBox="0 0 88 28"
              preserveAspectRatio="none"
            >
              <polyline
                :points="sparklinePoints(item.daily_weights)"
                fill="none"
                class="zone-sparkline__line"
              />
            </svg>
          </div>

          <p class="zone-summary-card__meta">
            <span v-if="item.zone.lomo != null">Lomo {{ item.zone.lomo }}</span>
            <span v-if="item.zone.variety">{{ item.zone.lomo != null ? ' · ' : '' }}{{ item.zone.variety }}</span>
          </p>

          <div
            v-if="item.prediction_days || item.actual_net_weight"
            class="zone-summary-card__stats"
          >
            <div v-if="item.prediction_days" class="zone-stat">
              <span class="zone-stat__label">Proyectado</span>
              <span class="zone-stat__value">{{ formatKg(toKg(item.total_net_weight)) }}</span>
            </div>
            <div class="zone-stat">
              <span class="zone-stat__label">Total recogido acumulado</span>
              <span class="zone-stat__value">{{ formatKg(toKg(item.actual_net_weight || 0)) }}</span>
            </div>
            <div v-if="item.prediction_days" class="zone-stat">
              <span class="zone-stat__label">Score medio</span>
              <span class="zone-stat__value">{{ formatScore(item.avg_score) }}</span>
            </div>
          </div>

          <p v-if="item.prediction_days && item.peak_day" class="zone-summary-card__peak">
            Mayor incremento: {{ formatShortDate(item.peak_day) }}
          </p>
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import {
  PREDICTION_HORIZON_DAYS,
  predictionDateFromToday,
} from '../../utils/predictionHorizon';

const MONTHS_ES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

// TODO: eliminar cuando la integración con el modelo esté activa
const USE_MOCK_FALLBACK = true;

const MOCK_DAILY_FACTORS = [0.82, 0.94, 0.74, 0.59, 0.91, 1.24, 0.86, 0.77, 0.65, 0.98];

function mockPeakDayOffset() {
  let peakIndex = 0;
  let peakValue = MOCK_DAILY_FACTORS[0];
  MOCK_DAILY_FACTORS.forEach((value, index) => {
    if (value > peakValue) {
      peakValue = value;
      peakIndex = index;
    }
  });
  return peakIndex;
}

const MOCK_PEAK_DAY_OFFSET = mockPeakDayOffset();

function finalizeSummaries(summaries) {
  const withPredictions = summaries.filter((item) => item.prediction_days > 0);
  const totalPredicted = withPredictions.reduce(
    (sum, item) => sum + (item.total_net_weight || 0),
    0,
  );

  withPredictions.forEach((item) => {
    item.contribution_percent =
      totalPredicted > 0
        ? Math.round((item.total_net_weight / totalPredicted) * 1000) / 10
        : 0;
  });

  return summaries;
}

function buildMockZoneSummaries(zones, nextHarvestZoneId) {
  const multipliers = [1.0, 0.85, 1.15, 0.7, 1.05, 0.92, 1.2, 0.78];
  const actualFactors = [1.15, 0.9, 1.35, 0.62, 1.05, 0.88, 1.25, 0.72];

  const summaries = (zones || []).map((zone, index) => {
    const factor = multipliers[index % multipliers.length];
    const baseKg = 2880 * factor;
    const daily_weights = MOCK_DAILY_FACTORS.map((dailyFactor) =>
      Math.round(baseKg * dailyFactor * 100),
    );
    const total_net_weight = daily_weights.reduce((sum, value) => sum + value, 0);
    const actualFactor = actualFactors[index % actualFactors.length];

    return {
      zone,
      prediction_days: daily_weights.length,
      total_net_weight,
      actual_net_weight: Math.round(baseKg * actualFactor * 85),
      avg_score: Math.round((7 + (index % 3) * 0.5) * 10) / 10,
      peak_day: predictionDateFromToday(MOCK_PEAK_DAY_OFFSET),
      next_prediction_date: predictionDateFromToday(0),
      is_next_harvest: zone.id === nextHarvestZoneId,
      contribution_percent: 0,
      daily_weights,
    };
  });

  return finalizeSummaries(summaries);
}

function buildDemoZones(finca) {
  const prefix = finca?.code || 'DEMO';
  const baseId = Number(finca?.id) || 0;
  const labels = ['Norte', 'Centro', 'Sur', 'Este', 'Oeste'];
  const varieties = ['DUNA', 'MARISMA', 'REDSAYRA', 'DUNA', 'MARISMA'];

  return labels.map((label, index) => ({
    id: baseId * 1000 + index + 1,
    name: `${prefix} ${label}`,
    code: `${prefix}-${index + 1}`,
    lomo: index + 1,
    variety: varieties[index % varieties.length],
  }));
}

function resolveMockNextHarvestZoneId(zones) {
  if (!zones?.length) return null;
  const sorted = [...zones].sort((a, b) => {
    const lomoA = a.lomo != null ? a.lomo : 10000;
    const lomoB = b.lomo != null ? b.lomo : 10000;
    if (lomoA !== lomoB) return lomoA - lomoB;
    return String(a.code || '').localeCompare(String(b.code || ''));
  });
  return sorted[0]?.id ?? null;
}

export default {
  name: 'FarmPredictionsWidget',

  props: {
    finca: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      summary: null,
      availableZones: [],
      loading: false,
      error: null,
      sortBy: 'rotation',
      sortOptions: [
        { value: 'rotation', label: 'Rotación' },
        { value: 'kg', label: 'Kg' },
        { value: 'score', label: 'Score' },
      ],
    };
  },

  computed: {
    displayZones() {
      const zones = this.availableZones;
      const apiZones = this.summary?.zones || [];
      const apiHasData = apiZones.some((item) => item.prediction_days > 0);

      if (apiHasData && !USE_MOCK_FALLBACK) {
        return apiZones;
      }

      if (USE_MOCK_FALLBACK && zones.length) {
        const nextId = resolveMockNextHarvestZoneId(zones);
        return buildMockZoneSummaries(zones, nextId);
      }

      return apiZones;
    },

    sortedZones() {
      const list = [...this.displayZones];

      if (this.sortBy === 'kg') {
        return list.sort(
          (a, b) => (b.total_net_weight || 0) - (a.total_net_weight || 0),
        );
      }

      if (this.sortBy === 'score') {
        return list.sort((a, b) => (b.avg_score || 0) - (a.avg_score || 0));
      }

      return list.sort((a, b) => {
        if (a.is_next_harvest) return -1;
        if (b.is_next_harvest) return 1;
        const lomoA = a.zone?.lomo != null ? a.zone.lomo : 10000;
        const lomoB = b.zone?.lomo != null ? b.zone.lomo : 10000;
        if (lomoA !== lomoB) return lomoA - lomoB;
        return String(a.zone?.code || '').localeCompare(String(b.zone?.code || ''));
      });
    },

    hasPredictionData() {
      return this.displayZones.some((item) => item.prediction_days > 0);
    },

    farmTotalKg() {
      return this.displayZones
        .filter((item) => item.prediction_days > 0)
        .reduce((sum, item) => sum + this.toKg(item.total_net_weight), 0);
    },

    farmTotalActualKg() {
      return this.displayZones.reduce(
        (sum, item) => sum + this.toKg(item.actual_net_weight || 0),
        0,
      );
    },

    horizonDays() {
      return PREDICTION_HORIZON_DAYS;
    },

    farmAverageScore() {
      const withData = this.displayZones.filter((item) => item.prediction_days > 0);
      if (!withData.length) return '—';
      const total = withData.reduce((sum, item) => sum + (item.avg_score || 0), 0);
      const avg = total / withData.length;
      return this.formatScore(avg);
    },

    nextHarvestLabel() {
      const next = this.displayZones.find((item) => item.is_next_harvest);
      if (!next) return '—';
      return next.zone?.name || next.zone?.code || '—';
    },
  },

  watch: {
    finca: {
      immediate: true,
      handler() {
        this.loadSummary();
      },
    },
  },

  methods: {
    async loadSummary() {
      if (!this.finca?.id) {
        this.summary = null;
        this.availableZones = [];
        return;
      }

      this.loading = true;
      this.error = null;

      await this.resolveAvailableZones();

      try {
        const res = await this.$http.get(
          `${this.$hostname}/farmlands/${this.finca.id}/predicciones/`,
        );
        const data = res.data || {};
        if (Array.isArray(data.zones)) {
          this.summary = data;
        } else {
          this.summary = null;
        }
      } catch (err) {
        if (err?.response?.status === 403) {
          this.error = 'No tienes permisos para ver predicciones.';
        } else if (!USE_MOCK_FALLBACK) {
          this.error = 'No se pudieron cargar las predicciones de la finca.';
        }
        this.summary = null;
        console.error('Error cargando predicciones de finca:', err);
      } finally {
        this.loading = false;
      }
    },

    async resolveAvailableZones() {
      const zonesFromFinca = Array.isArray(this.finca?.zones) ? this.finca.zones : [];
      if (zonesFromFinca.length) {
        this.availableZones = zonesFromFinca;
        return;
      }

      try {
        const res = await this.$http.get(
          `${this.$hostname}/farmlands/${this.finca.id}/zones/`,
        );
        this.availableZones = Array.isArray(res.data) ? res.data : [];
      } catch (err) {
        console.error('Error cargando zonas de la finca:', err);
        this.availableZones = [];
      }

      if (USE_MOCK_FALLBACK && !this.availableZones.length) {
        this.availableZones = buildDemoZones(this.finca);
      }
    },

    selectZone(zone) {
      this.$emit('select-zone', zone);
    },

    sparklinePoints(dailyWeights) {
      if (!Array.isArray(dailyWeights) || !dailyWeights.length) return '';

      const width = 88;
      const height = 28;
      const padding = 2;
      const max = Math.max(...dailyWeights, 1);
      const step = dailyWeights.length === 1 ? 0 : (width - padding * 2) / (dailyWeights.length - 1);

      return dailyWeights
        .map((weight, index) => {
          const x = padding + step * index;
          const y = height - padding - (weight / max) * (height - padding * 2);
          return `${x},${y}`;
        })
        .join(' ');
    },

    toKg(grams) {
      return Math.round((Number(grams) || 0) / 10) / 100;
    },

    formatKg(kg) {
      const value = Number(kg) || 0;
      return `${value.toLocaleString('es-ES', { maximumFractionDigits: 0 })} kg`;
    },

    formatScore(score) {
      const value = Number(score);
      if (Number.isNaN(value)) return '—';
      return Number.isInteger(value) ? String(value) : value.toFixed(1).replace('.', ',');
    },

    formatPercent(value) {
      const num = Number(value);
      if (Number.isNaN(num)) return '—';
      return `${num.toLocaleString('es-ES', { maximumFractionDigits: 1 })}%`;
    },

    formatShortDate(dateStr) {
      if (!dateStr) return '—';
      const date = new Date(dateStr);
      if (Number.isNaN(date.getTime())) return '—';
      return `${date.getDate()} ${MONTHS_ES[date.getMonth()]}`;
    },
  },
};
</script>

<style scoped>
.farm-prediction-view {
  padding: 12px 14px 24px;
  max-width: 900px;
  margin: 0 auto;
  color: #1a1a1a;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
}

.prediction-header {
  margin-bottom: 16px;
}

.prediction-title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #107631;
}

.prediction-subtitle {
  margin: 4px 0 0;
  font-size: 0.9rem;
  color: #444;
}

.prediction-state {
  text-align: center;
  padding: 40px 16px;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 12px;
  color: #555;
}

.prediction-state--error {
  color: #b45309;
}

.prediction-kpis {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-bottom: 12px;
}

.kpi-card {
  background: rgba(255, 255, 255, 0.92);
  border-radius: 10px;
  padding: 10px 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: center;
}

.kpi-card--highlight {
  background: rgba(16, 118, 49, 0.12);
  border: 1px solid rgba(16, 118, 49, 0.35);
}

.kpi-label {
  font-size: 0.65rem;
  color: #666;
  line-height: 1.2;
}

.kpi-value {
  font-size: 0.95rem;
  font-weight: 700;
  color: #107631;
}

.sort-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
}

.sort-toolbar__label {
  font-size: 0.72rem;
  color: #666;
  margin-right: 2px;
}

.sort-btn {
  border: 1px solid rgba(16, 118, 49, 0.35);
  background: rgba(255, 255, 255, 0.9);
  color: #107631;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;
}

.sort-btn--active {
  background: #107631;
  color: #fff;
  border-color: #107631;
}

.prediction-hint {
  margin: 0 0 14px;
  font-size: 0.72rem;
  color: #666;
}

.zone-summary-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.zone-summary-card {
  width: 100%;
  text-align: left;
  background: rgba(255, 255, 255, 0.92);
  border: 2px solid transparent;
  border-radius: 12px;
  padding: 12px 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.zone-summary-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.zone-summary-card--next {
  border-color: #107631;
  background: rgba(16, 118, 49, 0.08);
}

.zone-summary-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.zone-summary-card__header {
  flex: 1;
  min-width: 0;
}

.zone-summary-card__name {
  display: block;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1a1a1a;
}

.zone-summary-card__tags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
}

.zone-summary-card__badge {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #fff;
  background: #107631;
  border-radius: 999px;
  padding: 3px 8px;
}

.zone-summary-card__contrib {
  font-size: 0.68rem;
  font-weight: 700;
  color: #107631;
  background: rgba(16, 118, 49, 0.12);
  border-radius: 999px;
  padding: 3px 8px;
}

.zone-sparkline {
  width: 88px;
  height: 28px;
  flex-shrink: 0;
}

.zone-sparkline__line {
  stroke: #107631;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.zone-summary-card__meta {
  margin: 6px 0 10px;
  font-size: 0.75rem;
  color: #666;
}

.zone-summary-card__stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.zone-stat {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.zone-stat__label {
  font-size: 0.62rem;
  color: #888;
}

.zone-stat__value {
  font-size: 0.85rem;
  font-weight: 700;
  color: #107631;
}

.zone-summary-card__peak {
  margin: 8px 0 0;
  font-size: 0.72rem;
  color: #555;
}

.zone-summary-card--empty {
  opacity: 0.85;
}

.zone-summary-card__peak--empty {
  color: #999;
  font-style: italic;
}

@media (max-width: 900px) {
  .prediction-kpis {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 600px) {
  .prediction-kpis {
    grid-template-columns: 1fr;
  }

  .zone-summary-card__stats {
    grid-template-columns: 1fr;
  }

  .zone-summary-card__top {
    flex-direction: column;
  }

  .zone-sparkline {
    width: 100%;
    height: 32px;
  }
}
</style>
